import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Breadcrumbs,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Employee from './Employee';
import RolePosition from './RolePosition';
import Schedule from './Schedule';
import WorkforceSchedulingSettings from './WorkforceSchedulingSettings';
import WeeklyLaborDashboard from './WeeklyLaborDashboard';
import { getTenantId } from '../ApSetup/setupUtils';
import {
  fetchWorkforceBusinessUnits,
  formatBusinessUnitLabel,
  resolveWorkforceScope,
} from './workforceBusinessUnitUtils';
import {
  DEFAULT_WEEK_END_DAY,
  DEFAULT_WEEK_START_DAY,
  buildWorkWeekDays,
  fetchTenantWorkWeek,
  getWeekStartDate,
} from './workforceWeekUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateToolbarBuFieldSx,
  harmonateSwitchSx,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';

const STATIC_SUMMARY_CARDS = [
  { id: 1, metric: 'Required Hours Today', value: '0' },
  { id: 2, metric: 'Scheduled Hours Today', value: '0' },
  { id: 3, metric: 'Variance Today (Over / Under)', value: '0' },
  { id: 4, metric: 'Total Employees', value: '0' },
  { id: 5, metric: 'Employees Available Today', value: '0' },
];

/** Variance Today = Required Hours Today − Scheduled Hours Today (negative = under). */
const formatHoursVariance = (requiredHours, scheduledHours) => {
  const required = Number(requiredHours);
  const scheduled = Number(scheduledHours);
  if (!Number.isFinite(required) || !Number.isFinite(scheduled)) return '0';
  const variance = required - scheduled;
  if (variance === 0) return '0';
  return String(variance);
};

const menuOptions = [
  'Position',
  'Employee',
  // 'Skills',
  // 'Shifts',
  'Scheduling Settings',
  'PMS Data',
];

/** Coverage dashboard shows only Met and Under Coverage (server may still return other buckets). */
const formatCoverageRowsForDisplay = (summary) =>
  (Array.isArray(summary) ? summary : [])
    .filter((item) => item.status === 'Met' || item.status === 'Under')
    .map((item, index) => ({
      id: index + 1,
      status: item.status === 'Under' ? 'Under Coverage' : item.status || '',
      value: typeof item.percentage === 'number' ? item.percentage : Number(item.percentage) || 0,
      color: item.color || '#9e9e9e',
    }));

const getWeekStartForDay = (baseDate = new Date(), startDay = DEFAULT_WEEK_START_DAY) =>
  getWeekStartDate(baseDate, startDay);

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const buildWeekDates = (weekStart, dayCount = 7) =>
  Array.from({ length: Math.max(dayCount, 1) }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

const formatWeekRangeLabel = (weekDates) => {
  if (!weekDates?.length) return '';
  const start = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const end = weekDates[weekDates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Weekly View (${start} - ${end})`;
};

const WorkforceOperations = () => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [summaryCards, setSummaryCards] = useState(STATIC_SUMMARY_CARDS);
  const [dashboardSummaryLoading, setDashboardSummaryLoading] = useState(false);
  const [employeesScheduledTodayRows, setEmployeesScheduledTodayRows] = useState([]);
  const [scheduledTodayLoading, setScheduledTodayLoading] = useState(false);
  const [employeesOnLeaveRows, setEmployeesOnLeaveRows] = useState([]);
  const [onLeaveLoading, setOnLeaveLoading] = useState(false);
  const [coverageRows, setCoverageRows] = useState([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [weekStartDay, setWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);
  const [weekEndDay, setWeekEndDay] = useState(DEFAULT_WEEK_END_DAY);
  const [weekAnchorDate, setWeekAnchorDate] = useState(() => getWeekStartForDay(new Date()));
  const [showWeeklyLaborDashboard, setShowWeeklyLaborDashboard] = useState(false);

  const refreshDashboard = useCallback(() => {
    setDashboardRefreshKey((key) => key + 1);
  }, []);

  const isMenuOpen = useMemo(() => Boolean(menuAnchor), [menuAnchor]);
  const hasBusinessUnit = Boolean(
    resolveWorkforceScope(selectedBusinessUnit).propertyId
  );
  const isEmployeePage = currentView === 'employee';
  const tenantId = useMemo(() => getTenantId(), []);

  useEffect(() => {
    let cancelled = false;
    const loadWeekStart = async () => {
      if (!tenantId) return;
      try {
        const token = localStorage.getItem('token');
        const week = await fetchTenantWorkWeek(process.env.REACT_APP_BACKEND_URL, token, tenantId);
        if (cancelled) return;
        setWeekStartDay(week.weekStartDay);
        setWeekEndDay(week.weekEndDay);
        setWeekAnchorDate((prev) => getWeekStartForDay(prev, week.weekStartDay));
      } catch {
        if (!cancelled) {
          setWeekStartDay(DEFAULT_WEEK_START_DAY);
          setWeekEndDay(DEFAULT_WEEK_END_DAY);
        }
      }
    };
    loadWeekStart();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);
  const dashboardPropertyId = useMemo(
    () => resolveWorkforceScope(selectedBusinessUnit).propertyId,
    [selectedBusinessUnit]
  );
  const dashboardWorkDayCount = useMemo(
    () => buildWorkWeekDays(weekStartDay, weekEndDay).dayKeys.length,
    [weekStartDay, weekEndDay]
  );
  const dashboardWeekDates = useMemo(
    () => buildWeekDates(weekAnchorDate, dashboardWorkDayCount),
    [dashboardWorkDayCount, weekAnchorDate]
  );
  const dashboardWeekStartDate = useMemo(() => toIsoDate(weekAnchorDate), [weekAnchorDate]);
  const dashboardWeekEndDate = useMemo(
    () => toIsoDate(dashboardWeekDates[dashboardWeekDates.length - 1] || weekAnchorDate),
    [dashboardWeekDates, weekAnchorDate]
  );
  const dashboardWeekLabel = useMemo(() => formatWeekRangeLabel(dashboardWeekDates), [dashboardWeekDates]);
  const dashboardPropertyLabel = useMemo(
    () => resolveWorkforceScope(selectedBusinessUnit).label,
    [selectedBusinessUnit]
  );

  const shiftDashboardWeek = useCallback((direction) => {
    setWeekAnchorDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + direction * 7);
      return getWeekStartForDay(next, weekStartDay);
    });
  }, [weekStartDay]);

  useEffect(() => {
    let cancelled = false;
    const loadBusinessUnits = async () => {
      try {
        setBusinessUnitsLoading(true);
        const tenantId = getTenantId();
        if (!tenantId) {
          if (!cancelled) setBusinessUnits([]);
          return;
        }
        const units = await fetchWorkforceBusinessUnits(tenantId);
        if (!cancelled) setBusinessUnits(units);
      } catch (error) {
        console.error('Error fetching business units:', error);
        if (!cancelled) setBusinessUnits([]);
      } finally {
        if (!cancelled) setBusinessUnitsLoading(false);
      }
    };

    loadBusinessUnits();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const propertyId = resolveWorkforceScope(selectedBusinessUnit).propertyId;
    const tenantId = localStorage.getItem('tenant_id');
    const token = localStorage.getItem('token');

    if (!propertyId || !tenantId || !token) {
      setSummaryCards((prev) =>
        prev.map((card) =>
          card.id === 2 || card.id === 3 || card.id === 4 || card.id === 5 ? { ...card, value: '0' } : card
        )
      );
      setEmployeesScheduledTodayRows([]);
      setEmployeesOnLeaveRows([]);
      setCoverageRows([]);
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    const base = process.env.REACT_APP_BACKEND_URL;
    const query =
      `tenant_id=${encodeURIComponent(tenantId)}` + `&property_id=${encodeURIComponent(propertyId)}`;

    let cancelled = false;

    const loadDashboard = async () => {
      setDashboardSummaryLoading(true);
      setScheduledTodayLoading(true);
      setOnLeaveLoading(true);
      setCoverageLoading(true);

      try {
        const [totalResp, availableResp, scheduledHoursResp, scheduledResp, onLeaveResp, coverageResp] =
          await Promise.all([
            fetch(`${base}conductor/employees/total-by-property?${query}`, { method: 'GET', headers }),
            fetch(`${base}conductor/employees/available-by-property?${query}`, { method: 'GET', headers }),
            fetch(`${base}conductor/employees/scheduled-hours-by-property?${query}`, { method: 'GET', headers }),
            fetch(`${base}conductor/employees/scheduled-by-property?${query}`, { method: 'GET', headers }),
            fetch(`${base}conductor/employees/on-leave-by-property?${query}`, { method: 'GET', headers }),
            fetch(`${base}conductor/employees/coverage-status-by-property?${query}`, { method: 'GET', headers }),
          ]);

        if (cancelled) return;

        if (totalResp.ok && availableResp.ok && scheduledHoursResp.ok) {
          const totalData = await totalResp.json();
          const availableData = await availableResp.json();
          const scheduledHoursData = await scheduledHoursResp.json();
          const totalCount =
            typeof totalData?.total_employee_count === 'number'
              ? totalData.total_employee_count
              : Number(totalData?.total_employee_count) || 0;
          const availableCount =
            typeof availableData?.employees_available_count === 'number'
              ? availableData.employees_available_count
              : Number(availableData?.employees_available_count) || 0;
          const scheduledHours =
            scheduledHoursData?.total_scheduled_hours != null
              ? Number(scheduledHoursData.total_scheduled_hours)
              : 0;

          setSummaryCards((prev) => {
            const requiredHours = Number(prev.find((c) => c.id === 1)?.value) || 0;
            const scheduledValue = Number.isFinite(scheduledHours) ? scheduledHours : 0;
            const varianceValue = formatHoursVariance(requiredHours, scheduledValue);

            return prev.map((card) => {
              if (card.id === 2) return { ...card, value: String(scheduledValue) };
              if (card.id === 3) return { ...card, value: varianceValue };
              if (card.id === 4) return { ...card, value: String(totalCount) };
              if (card.id === 5) return { ...card, value: String(availableCount) };
              return card;
            });
          });
        } else {
          setSummaryCards((prev) =>
            prev.map((card) =>
              card.id === 2 || card.id === 3 || card.id === 4 || card.id === 5 ? { ...card, value: '0' } : card
            )
          );
        }

        if (scheduledResp.ok) {
          const scheduledData = await scheduledResp.json();
          const scheduledRows = Array.isArray(scheduledData?.employees)
            ? scheduledData.employees.map((row, index) => ({
                id: row.employee_assignment_id || `${row.employee_id || 'row'}-${index}`,
                employee_name: row.employee_name || '',
                shift: row.shift || '',
              }))
            : [];
          if (!cancelled) setEmployeesScheduledTodayRows(scheduledRows);
        } else if (!cancelled) {
          setEmployeesScheduledTodayRows([]);
        }

        if (onLeaveResp.ok) {
          const onLeaveData = await onLeaveResp.json();
          const leaveRows = Array.isArray(onLeaveData?.employees)
            ? onLeaveData.employees.map((row, index) => ({
                id: row.employee_leave_id || `${row.employee_id || 'row'}-${index}`,
                employee_name: row.employee_name || '',
                leave_type: row.leave_type || '',
              }))
            : [];
          if (!cancelled) setEmployeesOnLeaveRows(leaveRows);
        } else if (!cancelled) {
          setEmployeesOnLeaveRows([]);
        }

        if (coverageResp.ok) {
          const coverageData = await coverageResp.json();
          if (!cancelled) {
            setCoverageRows(formatCoverageRowsForDisplay(coverageData?.coverage_status_summary));
          }
        } else if (!cancelled) {
          setCoverageRows([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading workforce dashboard:', error);
          setSummaryCards((prev) =>
            prev.map((card) =>
              card.id === 2 || card.id === 3 || card.id === 4 || card.id === 5 ? { ...card, value: '0' } : card
            )
          );
          setEmployeesScheduledTodayRows([]);
          setEmployeesOnLeaveRows([]);
          setCoverageRows([]);
        }
      } finally {
        if (!cancelled) {
          setDashboardSummaryLoading(false);
          setScheduledTodayLoading(false);
          setOnLeaveLoading(false);
          setCoverageLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [selectedBusinessUnit, dashboardRefreshKey]);

  const handleMenuOpen = (event) => {
    if (!hasBusinessUnit) return;
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  useEffect(() => {
    if (!hasBusinessUnit) {
      setMenuAnchor(null);
    }
  }, [hasBusinessUnit]);

  const handleOptionSelect = (option) => {
    const viewMap = {
      Position: 'rolePosition',
      Employee: 'employee',
      Skills: 'skills',
      Shifts: 'shifts',
      'Scheduling Settings': 'schedulingSettings',
      'PMS Data': 'pmsData',
    };
    setCurrentView(viewMap[option] || 'dashboard');
    handleMenuClose();
  };

  const handleBackToDashboard = useCallback(() => {
    setCurrentView('dashboard');
    refreshDashboard();
  }, [refreshDashboard]);

  const getPageLabel = () => {
    const pageLabelMap = {
      rolePosition: 'Position',
      employee: 'Employee',
      skills: 'Skills',
      shifts: 'Shifts',
      schedulingSettings: 'Scheduling Settings',
      schedule: 'Schedule',
      pmsData: 'PMS Data',
    };
    return pageLabelMap[currentView] || 'Workforce Operations';
  };

  const breadcrumbSx = {
    fontSize: '0.9rem',
    '& .MuiBreadcrumbs-li': { color: ht.white },
    '& a': { color: ht.white },
    '& .MuiBreadcrumbs-separator': { color: ht.white },
  };

  return (
    <Box sx={{ ...harmonatePageSx, p: 3, overflowY: 'auto' }}>
      {isEmployeePage ? (
        <Employee
          onBack={handleBackToDashboard}
          selectedBusinessUnit={selectedBusinessUnit}
          onEmployeeDataChanged={refreshDashboard}
        />
      ) : currentView === 'rolePosition' ? (
        <RolePosition onBack={handleBackToDashboard} selectedBusinessUnit={selectedBusinessUnit} />
      ) : currentView === 'schedule' ? (
        <Schedule
          selectedBusinessUnit={selectedBusinessUnit}
          onBack={handleBackToDashboard}
          dataRefreshKey={dashboardRefreshKey}
        />
      ) : currentView === 'schedulingSettings' ? (
        <>
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbSx}>
              <Link
                underline="hover"
                color="inherit"
                onClick={handleBackToDashboard}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white }}
              >
                <ArrowBackIcon sx={{ fontSize: '1rem' }} />
                Workforce Operations
              </Link>
              <Typography sx={{ color: ht.white }}>{getPageLabel()}</Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
              {getPageLabel()}
            </Typography>
          </Box>
          <WorkforceSchedulingSettings />
        </>
      ) : currentView === 'dashboard' ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
              Workforce Optimization
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showWeeklyLaborDashboard}
                    onChange={(event) => setShowWeeklyLaborDashboard(event.target.checked)}
                    sx={harmonateSwitchSx}
                  />
                }
                label="Weekly Labor Dashboard"
                sx={{
                  mr: 0.5,
                  whiteSpace: 'nowrap',
                  '& .MuiFormControlLabel-label': { color: ht.white, fontSize: '0.875rem' },
                }}
              />
              <Autocomplete
                size="small"
                options={businessUnits}
                value={selectedBusinessUnit}
                onChange={(event, newValue) => setSelectedBusinessUnit(newValue)}
                getOptionLabel={formatBusinessUnitLabel}
                isOptionEqualToValue={(option, value) =>
                  (option.propertyId || option.PROPERTY_ID) === (value?.propertyId || value?.PROPERTY_ID)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Business Unit"
                    placeholder="Select Business Unit"
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        ...(params.inputProps?.style || {}),
                        color: ht.white,
                        WebkitTextFillColor: ht.white,
                        caretColor: ht.white,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {businessUnitsLoading ? (
                            <CircularProgress color="inherit" size={18} sx={{ color: ht.white }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.75) !important',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${ht.white} !important`,
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${ht.white} !important`,
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.85) !important',
                        WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                        opacity: '1 !important',
                      },
                      '& input::-webkit-input-placeholder': {
                        color: 'rgba(255, 255, 255, 0.85) !important',
                        WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                        opacity: '1 !important',
                      },
                    }}
                  />
                )}
                sx={{
                  ...harmonateToolbarBuFieldSx,
                  minWidth: 300,
                  width: 300,
                  '& .MuiAutocomplete-popupIndicator': { color: ht.white },
                  '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.75)' },
                  '& .MuiAutocomplete-endAdornment': { right: 6 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'transparent',
                    borderRadius: '8px !important',
                    height: 40,
                    alignItems: 'center',
                    color: ht.white,
                    paddingRight: '36px !important',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.75) !important',
                      borderWidth: '1px',
                      borderRadius: '8px',
                    },
                    '&:hover fieldset': { borderColor: `${ht.white} !important` },
                    '&.Mui-focused fieldset': {
                      borderColor: `${ht.white} !important`,
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input, & .MuiInputBase-input, & input': {
                    px: 1.25,
                    fontSize: '0.875rem',
                    color: `${ht.white} !important`,
                    WebkitTextFillColor: ht.white,
                    py: 0,
                    caretColor: ht.white,
                    '&::placeholder': {
                      color: 'rgba(255, 255, 255, 0.85) !important',
                      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                      opacity: '1 !important',
                    },
                    '&::-webkit-input-placeholder': {
                      color: 'rgba(255, 255, 255, 0.85) !important',
                      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                      opacity: '1 !important',
                    },
                  },
                }}
                disabled={businessUnitsLoading}
              />
              <Button
                variant="contained"
                onClick={() => setCurrentView('schedule')}
                sx={{ ...harmonateProceedButtonSx, minHeight: 40, height: 40, whiteSpace: 'nowrap' }}
              >
                Schedule
              </Button>
              <Tooltip title={hasBusinessUnit ? 'More options' : 'Select a business unit to navigate'}>
                <span>
                  <IconButton
                    aria-label="workforce operations options"
                    aria-controls={isMenuOpen ? 'workforce-operations-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen ? 'true' : undefined}
                    onClick={handleMenuOpen}
                    disabled={!hasBusinessUnit}
                    sx={{ ...harmonatePlainIconButtonSx, width: 40, height: 40, boxSizing: 'border-box' }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {showWeeklyLaborDashboard ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton
                  size="small"
                  aria-label="Previous week"
                  onClick={() => shiftDashboardWeek(-1)}
                  disabled={!dashboardPropertyId}
                  sx={harmonatePlainIconButtonSx}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 200, textAlign: 'center', color: ht.white }}>
                  {dashboardWeekLabel}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Next week"
                  onClick={() => shiftDashboardWeek(1)}
                  disabled={!dashboardPropertyId}
                  sx={harmonatePlainIconButtonSx}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <WeeklyLaborDashboard
                tenantId={tenantId}
                propertyId={dashboardPropertyId}
                propertyName={dashboardPropertyLabel}
                weekStartDate={dashboardWeekStartDate}
                weekEndDate={dashboardWeekEndDate}
                weekDates={dashboardWeekDates}
                refreshKey={dashboardRefreshKey}
              />
            </>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {summaryCards.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.id}>
                    <Card elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.metric}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: ht.text }}>
                          {(item.id === 2 || item.id === 3 || item.id === 4 || item.id === 5) &&
                          dashboardSummaryLoading ? (
                            <CircularProgress size={22} sx={{ color: ht.blue }} />
                          ) : (
                            item.value
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  ...harmonateCardSx,
                  bgcolor: ht.white,
                  p: 2,
                  height: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5, color: ht.text, flexShrink: 0 }}>
                  Employees Scheduled Today
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <DataGridPro
                    rows={employeesScheduledTodayRows}
                    columns={[
                      { field: 'employee_name', headerName: 'Employee Name', flex: 1, resizable: true },
                      { field: 'shift', headerName: 'Shift', width: 100, resizable: true },
                    ]}
                    rowHeight={38}
                    hideFooter
                    loading={scheduledTodayLoading}
                    disableRowSelectionOnClick
                    sx={{ ...harmonateDataGridSx, height: '100%', border: 'none' }}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  ...harmonateCardSx,
                  bgcolor: ht.white,
                  p: 2,
                  height: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5, color: ht.text, flexShrink: 0 }}>
                  Employees on Leave Today
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <DataGridPro
                    rows={employeesOnLeaveRows}
                    columns={[
                      { field: 'employee_name', headerName: 'Employee Name', flex: 1, resizable: true },
                      { field: 'leave_type', headerName: 'Leave Type', width: 120, resizable: true },
                    ]}
                    rowHeight={38}
                    hideFooter
                    loading={onLeaveLoading}
                    disableRowSelectionOnClick
                    sx={{ ...harmonateDataGridSx, height: '100%', border: 'none' }}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, height: 320 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: ht.text }}>
                  Coverage Status Today
                </Typography>
                {coverageLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress size={28} sx={{ color: ht.blue }} />
                  </Box>
                ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {coverageRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No positions requiring coverage today.
                    </Typography>
                  ) : null}
                  {coverageRows.map((item) => (
                    <Box key={item.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.status}
                        </Typography>
                        <Chip
                          label={`${item.value}%`}
                          size="small"
                          sx={{
                            bgcolor: `${item.color}22`,
                            color: item.color,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.value}
                        sx={{
                          height: 8,
                          borderRadius: 10,
                          bgcolor: ht.listBg,
                          '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 10 },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                )}
              </Paper>
            </Grid>
              </Grid>
            </>
          )}

          <Menu
            id="workforce-operations-menu"
            anchorEl={menuAnchor}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                borderRadius: ht.fieldRadius,
                '& .MuiMenuItem-root': { fontSize: '0.875rem', color: ht.text },
              },
            }}
          >
            {menuOptions.map((option) => (
              <MenuItem key={option} onClick={() => handleOptionSelect(option)}>
                {option}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbSx}>
              <Link
                underline="hover"
                color="inherit"
                onClick={handleBackToDashboard}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white }}
              >
                <ArrowBackIcon sx={{ fontSize: '1rem' }} />
                Workforce Operations
              </Link>
              <Typography sx={{ color: ht.white }}>{getPageLabel()}</Typography>
            </Breadcrumbs>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
              {getPageLabel()}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...harmonateCardSx,
              bgcolor: ht.white,
              flex: 1,
              p: 3,
              mb: 3,
              minHeight: '220px',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {getPageLabel()} page POC is ready for wiring next.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default WorkforceOperations;
